import React from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { parseExchangeTime } from './marketdataTime';
import { CHART_RANGES } from './MarketdataRangeSelector';
import ChartCrosshair from './MarketdataChartCrosshair';
import { computeEma, computeSupportResistance } from './marketdataChartAnalysis';

// Kerzen-Datum in ein lesbares Label umwandeln - 1T/1W liefern Datum+Uhrzeit
// ("YYYY-MM-DD HH:mm:ss", Boersenzeitzone, siehe parseExchangeTime()), 1M/6M/
// 1J/5J liefern reines Datum ("YYYY-MM-DD") ohne Uhrzeit (siehe
// MarketdataChart.inc - analog MarketdataDetailPanel.js).
export function formatLabel(dateString, range) {
	if (!dateString) {
		return '';
	}
	const isIntraday = CHART_RANGES.find((r) => r.code === range)?.intraday ?? true;
	if (isIntraday) {
		const localDate = parseExchangeTime(dateString);
		let label = localDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
		if (range === '1W' || range === '1T') {
			label = localDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + ' ' + label;
		}
		return label;
	}
	const [year, month, day] = dateString.split('-');
	return `${day}.${month}.${(range === '5J') ? year : year.slice(2)}`;
}

function formatPrice(value) {
	return value == null ? '–' : value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatChange(value) {
	if (value == null) return '';
	const sign = value > 0 ? '+' : '';
	return sign + value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Einzelne Chart-Kachel im Charts-Modul (siehe MarketdataCharts.js) - selbe
// Darstellung wie das Detail-Panel in "Watchlist Live" (Recharts, EMA50/200,
// Support/Widerstand, Tooltip, Fadenkreuz), nur kompakter (User-Feedback).
// Bezieht die Kerzen ueber denselben Task wie das Detail-Panel
// (MarketdataChart.inc) - dieselbe Kerzen-Logik (Einschwing-Puffer bei
// Tages-Zeitebenen, Vortag-Anhang bei "1T") statt eines eigenen, abweichenden
// Code-Pfads (siehe MarketdataCharts.js).
//
// Fruehere Version nutzte bewusst reines SVG statt Recharts, da bei einer
// ganzen Watchlist gleichzeitig (10+ Kacheln) je eine eigene
// <ResponsiveContainer>-Instanz (ResizeObserver + Re-Layout pro Kachel)
// spuerbare Ladezeit verursacht hat, bei JEDEM Sichtbarwerden des Moduls neu
// (Module.js entlaedt beim Verstecken nicht, nur display:none). Diese
// Mehrbelastung wurde bewusst in Kauf genommen, um dieselbe Darstellung wie
// im Detail-Panel zu haben.
export default function MarketdataChartTile({ symbol, shortlabel, candles, range, change: liveChange, changePercentage: liveChangePercentage, onReload }) {
	// candles === null: Request fuer dieses Symbol laeuft noch (siehe
	// MarketdataCharts.js) - eigener Ladezustand pro Kachel, damit schnell
	// beantwortete Symbole nicht auf die langsamsten warten muessen.
	const loading = candles === null;

	// Volle Reihe INKLUSIVE der unsichtbaren Einschwing-Puffer-Kerzen vor dem
	// angefragten Zeitraum (row.visible === false, siehe
	// FmpClient::getChartForRange()) - fuer die EMA-Berechnung noetig, damit
	// sie bei kurzen Zeitebenen von Anfang an eingeschwungen ist. Erst danach
	// wird auf die tatsaechlich sichtbaren Kerzen gefiltert (analog
	// MarketdataDetailPanel.js).
	const fullChartData = React.useMemo(() => (candles || [])
		.filter((row) => row.close != null)
		.map((row) => ({ ...row, time: row.time ?? formatLabel(row.date, range) })), [candles, range]);
	const visibleChartData = React.useMemo(() => fullChartData.filter((row) => row.visible !== false), [fullChartData]);

	const last = visibleChartData[visibleChartData.length - 1]?.close;
	const first = visibleChartData[0]?.close;

	// Hoch/Tief des eingestellten Zeitraums (Chat vom 2026-08-11).
	const extremes = React.useMemo(() => {
		let high = null;
		let low = null;
		for (const row of visibleChartData) {
			const h = row.high != null ? row.high : row.close;
			const l = row.low != null ? row.low : row.close;
			if (h != null) high = high == null ? h : Math.max(high, h);
			if (l != null) low = low == null ? l : Math.min(low, l);
		}
		return { high, low };
	}, [visibleChartData]);
	// Chat vom 2026-08-07 (Bugreport: "Veraenderung" wich stark vom richtigen
	// Wert in Watchlist Live ab) - ZUERST die echte Tages-Veraenderung (vs.
	// Vortagesschluss) aus MarketdataCharts.inc/live-Quote nehmen, exakt
	// dieselbe wie in Watchlist Live. Der erste/letzte-Kerze-Vergleich (change
	// ueber den sichtbaren Chart-Zeitraum) war eine ANDERE, verwirrende Metrik
	// und wurde zusaetzlich durch verzerrte Alt-Kerzen in der Selbst-
	// Mitschnitt-Historie anfaellig (siehe Silber-Chart-Bugreport) - bleibt nur
	// noch als Fallback, falls (noch) keine Live-Quote vorliegt.
	const change = liveChange ?? ((last != null && first != null) ? last - first : null);
	const changePercent = liveChangePercentage ?? ((change != null && first) ? (change / first) * 100 : null);
	// Chat vom 2026-08-07 (Folgebug: Linienfarbe zeigte Rot bei tatsaechlich
	// positivem Tageswert) - nutzte bisher DENSELBEN falschen Chart-Fenster-
	// Vergleich wie change/changePercent vorher. Jetzt dieselbe echte
	// Tages-Veraenderung wie oben - reagiert automatisch/live, sobald "change"
	// (aus dem Live-Quote-Push, siehe MarketdataCharts.js::handleQuote())
	// die Vorzeichen-Seite wechselt, kein zusaetzlicher Code noetig.
	const lineColor = (change == null || change === 0) ? '#5f8c6c' : (change > 0 ? '#1a7f37' : '#cf222e');
	// Endpunkt-Farbe (Chat vom 2026-08-10, Nutzerwunsch): muss sich klar von der
	// Linie abheben. Bei rotem (fallendem) Chart Blau, sonst - Linie ist gruen/
	// neutral - Orange (Blau war vom Gruen kaum zu unterscheiden). Weisser Rand
	// (siehe dot-Renderer) gibt zusaetzlich den kleinen Abstand zur Linie.
	const endDotColor = lineColor === '#cf222e' ? '#2f81f7' : '#fd7e14';

	const isIntradayRange = CHART_RANGES.find((r) => r.code === range)?.intraday ?? true;

	// EMA50/200 sind an Tages-Kerzen gebunden (50/200 Handelstage) - bei
	// Intraday-Zeitebenen (1m/1T/1W) waeren das nur ein paar Stunden, keine
	// Marke, auf die Charttechniker tatsaechlich schauen (siehe
	// MarketdataDetailPanel.js), daher dort erst gar nicht berechnen.
	const { data: withEma50, hasValues: hasEma50Full } = React.useMemo(
		() => (isIntradayRange ? { data: fullChartData, hasValues: false } : computeEma(fullChartData, 50)),
		[fullChartData, isIntradayRange]
	);
	const { data: withEma, hasValues: hasEma200Full } = React.useMemo(
		() => (isIntradayRange ? { data: withEma50, hasValues: false } : computeEma(withEma50, 200)),
		[withEma50, isIntradayRange]
	);
	const visibleWithEma = React.useMemo(() => withEma.filter((row) => row.visible !== false), [withEma]);
	const hasEma50 = hasEma50Full && visibleWithEma.some((row) => row.ema50 != null);
	const hasEma200 = hasEma200Full && visibleWithEma.some((row) => row.ema200 != null);

	const { supports, resistances } = React.useMemo(
		() => computeSupportResistance(visibleChartData, last),
		[visibleChartData, last]
	);

	const [hoveredPrice, setHoveredPrice] = React.useState(null);

	return (
		<div className="marketdata-chart-tile">
			<div className="marketdata-chart-tile-header">
				<span className="marketdata-chart-tile-title">{shortlabel || symbol}</span>
				<span className="marketdata-chart-tile-header-right">
					{/* Reload je Chart (Nutzerwunsch, Chat vom 2026-08-10): setzt
					    die Ansicht dieses Charts zurueck (frische Historie vom
					    Server, z.B. nach ein paar Stunden offline). Links vom Kurs,
					    vertikal mit ihm zentriert. */}
					{onReload &&
						<i
							className="bi bi-arrow-repeat marketdata-chart-tile-reload"
							title="Chart neu laden"
							onClick={(e) => { e.stopPropagation(); onReload(symbol); }}
						/>
					}
					<span className="marketdata-chart-tile-price" style={{ color: lineColor }}>
						{formatPrice(last)}
						{change != null &&
							<span className="marketdata-chart-tile-change"> {formatChange(change)} ({formatChange(changePercent)}%)</span>
						}
					</span>
				</span>
			</div>
			{(extremes.high != null || extremes.low != null) &&
				<div className="marketdata-chart-tile-extremes">
					<span>T <strong className="marketdata-price-down">{formatPrice(extremes.low)}</strong></span>
					<span>H <strong className="marketdata-price-up">{formatPrice(extremes.high)}</strong></span>
				</div>
			}
			{loading &&
				<div className="marketdata-detail-hint">Chart wird geladen…</div>
			}
			{!loading && visibleChartData.length < 2 &&
				<div className="marketdata-detail-hint">Keine Chart-Daten verfügbar.</div>
			}
			{!loading && visibleChartData.length >= 2 &&
				<ResponsiveContainer width="100%" height="100%" className="marketdata-chart-tile-chart">
					<LineChart data={visibleWithEma} margin={{ top: 4, right: 0, left: 4, bottom: 0 }}>
						{/* padding.right (Nutzerfeedback, Chat vom 2026-08-07, Vorbild
						    TradingView) - etwas Luft zwischen letztem Kurspunkt und der
						    Wertachse, die Linie klebt sonst direkt am Rand. */}
						<XAxis dataKey="time" tick={{ fontSize: 9 }} minTickGap={40} padding={{ right: 14 }} />
						{/* orientation="right" (Nutzerfeedback, Chat vom 2026-08-07) -
						    Wertachse rechts statt links. */}
						<YAxis domain={['auto', 'auto']} tick={{ fontSize: 9 }} width={40} orientation="right" />
						<ChartCrosshair onHover={setHoveredPrice} />
						<Tooltip
							content={(props) => {
								const active = props.active && props.payload && props.payload.length > 0;
								if (!active) {
									return null;
								}
								return (
									<div className="marketdata-detail-chart-tooltip">
										<div>Zeit: {props.label}</div>
										<div style={{ color: lineColor, fontWeight: 600 }}>{formatPrice((props.payload.find((p) => p.dataKey === 'close') || props.payload[0]).value)}</div>
									</div>
								);
							}}
						/>
						{supports.map((level, index) => (
							<ReferenceLine key={'support-' + index} y={level} stroke="#1a7f37" strokeDasharray="1 3" strokeOpacity={0.65 - index * 0.2} />
						))}
						{resistances.map((level, index) => (
							<ReferenceLine key={'resistance-' + index} y={level} stroke="#cf222e" strokeDasharray="1 3" strokeOpacity={0.65 - index * 0.2} />
						))}
						{hoveredPrice != null &&
							<ReferenceLine y={hoveredPrice} stroke="#888" strokeDasharray="2 2" />
						}
						{(hasEma50 || hasEma200) &&
							<Legend verticalAlign="top" height={16} wrapperStyle={{ fontSize: 10 }} />
						}
						{hasEma50 &&
							<Line type="monotone" dataKey="ema50" name="EMA50" stroke="#d4a017" strokeDasharray="5 3" dot={false} strokeWidth={1} isAnimationActive={false} connectNulls />
						}
						{hasEma200 &&
							<Line type="monotone" dataKey="ema200" name="EMA200" stroke="#8a5cf6" strokeDasharray="5 3" dot={false} strokeWidth={1} isAnimationActive={false} connectNulls />
						}
						<Line
							type="monotone"
							dataKey="close"
							name="Kurs"
							stroke={lineColor}
							strokeWidth={2}
							isAnimationActive={false}
							/* Endpunkt am letzten Kurswert (Nutzerfeedback, Chat vom
							   2026-08-07, Vorbild TradingView) - ReferenceDot per
							   Zeit-Label war fehleranfaellig (schlaegt fehl, wenn sich das
							   Zeit-Label wiederholt, z.B. durch Live-Tick-Rundung auf
							   dieselbe Minute - Recharts trifft dann nicht zuverlaessig den
							   LETZTEN Punkt mit diesem Label). Stattdessen die uebliche
							   Recharts-Methode: eigener dot-Renderer, der die von Recharts
							   bereits berechneten Pixel-Koordinaten nutzt und NUR beim
							   letzten Index zeichnet (kein Punkt auf jeder Kerze - bei
							   1-Minuten-Aufloesung sonst viel zu voll). */
							dot={(dotProps) => {
								if (dotProps.index !== visibleWithEma.length - 1) {
									return null;
								}
								// Endpunkt bewusst in einer AUFFAELLIGEN, festen Farbe
								// (nicht in Linienfarbe) mit weissem Rand + groesserem
								// Radius - so ist auf einen Blick (v.a. mobil) erkennbar,
								// wo der Chart aktuell steht (Nutzerwunsch, Chat vom
								// 2026-08-10). Blau hebt sich klar von Linie (gruen/rot)
								// und EMA-Linien (gold/violett) ab.
								return <circle key="last-dot" cx={dotProps.cx} cy={dotProps.cy} r={4} fill={endDotColor} stroke="#fff" strokeWidth={1.5} />;
							}}
						/>
					</LineChart>
				</ResponsiveContainer>
			}
		</div>
	);
}
